import React, { useEffect, useState } from 'react';
import {
  ArrowUpRight,
  Image,
  PanelsTopLeft,
  MessageSquareText,
  RefreshCw,
  Activity,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, getErrorMessage, unwrapList } from '../services/api';
import { ErrorMessage, Loading } from '../components/Feedback';
import { PageHeader, Section } from '../components/ui';

const tiles = [
  {
    key: 'portfolios',
    label: 'Portfolio projects',
    icon: PanelsTopLeft,
    tint: 'bg-brand-50 text-brand-700',
    path: '/portfolios',
    hint: 'Manage case studies',
  },
  {
    key: 'banners',
    label: 'Active banners',
    icon: Image,
    tint: 'bg-accent-50 text-accent-700',
    path: '/banners',
    hint: 'Update website visuals',
  },
  {
    key: 'contacts',
    label: 'Contact enquiries',
    icon: MessageSquareText,
    tint: 'bg-slate-100 text-slate-700',
    path: '/contacts',
    hint: 'Review messages',
  },
];

const kindStyle = {
  Contact: 'bg-slate-100 text-slate-700',
  Portfolio: 'bg-brand-50 text-brand-700',
};

export default function Dashboard() {
  const [stats, setStats] = useState({ portfolios: 0, banners: 0, contacts: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const results = await Promise.allSettled([
        api.get('/api/portfolios'),
        api.get('/api/banners'),
        api.get('/api/contact-us'),
      ]);
      const vals = results.map((r) => (r.status === 'fulfilled' ? unwrapList(r.value) : []));
      setStats({
        portfolios: vals[0].length,
        banners: vals[1].filter((banner) => banner.isActive !== false).length,
        contacts: vals[2].length,
      });
      const allContacts = vals[2].map((x) => ({ ...x, _kind: 'Contact' }));
      const allPortfolios = vals[0].map((x) => ({ ...x, _kind: 'Portfolio' }));
      setRecent(
        [...allContacts, ...allPortfolios]
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 5),
      );
      const rejected = results.find((r) => r.status === 'rejected');
      if (rejected) setError(getErrorMessage(rejected.reason));
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Welcome back"
        description="Here’s what’s happening with your website today."
      >
        <button onClick={load} className="btn-secondary" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh data
        </button>
      </PageHeader>

      <ErrorMessage>{error}</ErrorMessage>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {tiles.map(({ key, label, icon: Icon, tint, path, hint }) => (
          <div key={key} className="card p-5 transition hover:border-brand-200 sm:p-6">
            <div className="flex items-start justify-between">
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tint}`}>
                <Icon size={21} />
              </div>
              <Link
                to={path}
                className="flex items-center gap-1 rounded-md text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline"
              >
                View all <ArrowUpRight size={14} />
              </Link>
            </div>
            <p className="mt-5 text-sm font-medium text-ink-muted">{label}</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight text-ink">
              {loading ? '—' : stats[key]}
            </p>
            <p className="mt-2 text-xs text-slate-500">{hint}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_.65fr]">
        <Section
          title="Recent activity"
          description="Latest portfolio updates and contact enquiries"
          action={<Activity size={19} className="text-slate-400" />}
        >
          {loading ? (
            <Loading />
          ) : recent.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-muted">
              No recent activity to show.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((item, i) => (
                <div key={item._id || i} className="flex items-center gap-3 px-5 py-3.5 sm:px-6">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${kindStyle[item._kind]}`}
                  >
                    {item._kind === 'Contact' ? (
                      <MessageSquareText size={18} />
                    ) : (
                      <PanelsTopLeft size={18} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">
                      {item.name || item.title || 'Untitled record'}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {item._kind} ·{' '}
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : 'Date unavailable'}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${kindStyle[item._kind]}`}
                  >
                    {item._kind}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Quick actions" description="Jump straight to the tasks you use most.">
          <div className="space-y-2 p-4 sm:p-5">
            {[
              { to: '/portfolios', label: 'Add a portfolio project', icon: PanelsTopLeft },
              { to: '/banners', label: 'Manage website banners', icon: Image },
              { to: '/contacts', label: 'Open contact inbox', icon: MessageSquareText },
            ].map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-3 rounded-lg border border-line p-3.5 transition hover:border-brand-300 hover:bg-brand-50"
              >
                <Icon size={17} className="text-brand-700" />
                <span className="flex-1 text-sm font-medium text-slate-700 group-hover:text-brand-800">
                  {label}
                </span>
                <ChevronRight size={15} className="text-slate-400 group-hover:text-brand-700" />
              </Link>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
